import NoCompany from '../../components/NoCompany';
import { EmptyState, Loading, ModuleHero, Notice, StatusPill } from '../../components/ui';
import { useApi } from '../../hooks/useApi';
import { formatDateTime } from '../../lib/format';
import { CARD, CARD_STYLE } from '../../lib/ui';
import type { PersonRef } from '../../types/workspace';

type Event = {
  id: string;
  action: string;
  detail: string;
  createdAt: string;
  actor: Pick<PersonRef, 'id' | 'name' | 'email'> | null;
  document: { id: string; code: string; title: string; fileName: string; version: number } | null;
  report: { id: string; version: number; grade: string; score: number; status: string } | null;
};

export default function EvidenceAudit() {
  const { data, loading, error, code } = useApi<{ isDemo: boolean; events: Event[] }>('/customer/evidence-audit');
  if (loading) return <Loading />;
  if (code === 'NO_COMPANY') return <NoCompany />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  return (
    <div>
      <ModuleHero eyebrow="Evidence trail" title="Evidence audit" description="A read-only history of document, verification and rating evidence changes in this workspace." />
      {data.events.length === 0 ? (
        <div className="mt-6"><EmptyState title="No evidence events yet" body="Activity appears here as documents are uploaded, reviewed and used in ratings." /></div>
      ) : (
        <ol className="mt-6 space-y-3">
          {data.events.map((event) => (
            <li key={event.id} className={`${CARD} p-4`} style={CARD_STYLE}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <StatusPill label={event.action.replace(/_/g, ' ').toLowerCase()} tone="pink" />
                  <p className="mt-2 text-sm font-semibold text-[#6b0030]">{event.detail}</p>
                  <p className="mt-1 text-xs text-[#9a6b82]">{event.actor?.name || event.actor?.email || 'Micus system'}</p>
                </div>
                <time className="text-xs text-[#9a6b82]">{formatDateTime(event.createdAt)}</time>
              </div>
              {(event.document || event.report) && <p className="mt-3 border-t border-[#fce7f3] pt-3 text-xs text-[#9a6b82]">{event.document ? `${event.document.title} · ${event.document.fileName} · v${event.document.version}` : `Rating v${event.report!.version} · ${event.report!.grade} · ${event.report!.score}/100`}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
